import { useState, useEffect } from "react";
import { ShieldAlert, X } from "lucide-react";

/**
 * Compliance-aware disclosure banner for Tyvena.
 * Reminds families that Tyvena is not yet on HIPAA-BAA-covered hosting, so they
 * should avoid entering SSNs, detailed diagnosis codes, or full medical records.
 *
 * Dismissed state is stored in sessionStorage so it reappears on each new session.
 *
 * Props:
 *  - variant: "chat" | "portal" | "compact" — controls copy & density
 */
export default function PHIDisclaimerBanner({ variant = "portal" }) {
  const storageKey = `myasher-phi-notice-dismissed-${variant}`;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(storageKey) === "1");
    } catch {
      /* SSR / private mode — noop */
    }
  }, [storageKey]);

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(storageKey, "1");
    } catch { /* noop */ }
    setDismissed(true);
  };

  if (dismissed) return null;

  const copy = {
    chat:
      "Asher is here to help you find care options — not to receive medical records. Please avoid sharing Social Security numbers, diagnosis codes, or detailed medical history.",
    portal:
      "Tyvena takes your privacy seriously. During our interim launch, please avoid uploading detailed medical records, SSNs, or lab results — general care needs, preferences, and provider selections are perfectly fine.",
    compact:
      "Please avoid entering SSNs, medical record numbers, or diagnosis codes here.",
  }[variant];

  return (
    <div
      data-testid={`phi-banner-${variant}`}
      className="relative flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/80 backdrop-blur-sm px-4 py-3 shadow-sm"
    >
      <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 pr-6">
        <p className="text-sm font-body text-amber-900 leading-relaxed">
          <span className="font-bold">Health-information notice · </span>
          {copy}
        </p>
      </div>
      <button
        data-testid={`phi-banner-${variant}-dismiss`}
        onClick={handleDismiss}
        aria-label="Dismiss notice"
        className="absolute top-2 right-2 p-1 rounded-full text-amber-700 hover:bg-amber-100 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
